import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ETIQUETA_TIPO_TRABAJO, type TipoLineaMaterial, type TipoTrabajo } from '@cmms/shared';
import { api } from '../servicios/api.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { Aviso } from '../componentes/piezas.js';

interface ElementoDetalle {
  id: string;
  nombre: string;
  tipo: TipoLineaMaterial;
  tipoActivo: string | null;
  creadoEl: string;
  usosOt: number;
  usosSolicitud: number;
}

interface UsoElemento {
  origen: 'OT' | 'SOLICITUD';
  refId: string;
  numero: number;
  titulo: string;
  estado: string;
  cantidad: number;
}

interface ListaDelElemento {
  tipoActivoId: string;
  tipoActivo: string;
  tipoTrabajo: TipoTrabajo | null;
}

const ETIQUETA_TIPO: Record<TipoLineaMaterial, string> = {
  HERRAMIENTA: 'Herramienta',
  MATERIAL: 'Material',
};

const ETIQUETA_ESTADO: Record<string, string> = {
  ABIERTA: 'Abierta',
  EN_ESPERA: 'En espera',
  EN_CURSO: 'En curso',
  COMPLETA: 'Completa',
  PENDIENTE: 'Pendiente',
  APROBADA: 'Aprobada',
  DECLINADA: 'Declinada',
};

/**
 * Detalle de un material o herramienta del catálogo: en qué listas de materiales está
 * (tipo de activo y tipo de trabajo) y en qué OT y solicitudes se planeó.
 */
export function DetalleMaterialCatalogo(): JSX.Element {
  const { id = '' } = useParams();

  const detalle = useQuery({
    queryKey: ['materiales-catalogo', 'detalle', id],
    queryFn: () => api.get<ElementoDetalle>(`/materiales-catalogo/${id}`),
  });

  const listas = useQuery({
    queryKey: ['materiales-catalogo', 'listas', id],
    queryFn: () => api.get<ListaDelElemento[]>(`/materiales-catalogo/${id}/listas`),
  });

  const usos = useQuery({
    queryKey: ['materiales-catalogo', 'usos', id],
    queryFn: () => api.get<UsoElemento[]>(`/materiales-catalogo/${id}/usos`),
  });

  if (detalle.isLoading) return <p className="campo-nota">Cargando…</p>;
  if (detalle.error || !detalle.data) {
    return (
      <>
        <BotonVolver a="/listas-materiales" etiqueta="Volver a materiales creados" />
        <Aviso tono="error">No se encontró el material o herramienta.</Aviso>
      </>
    );
  }

  const elemento = detalle.data;

  return (
    <>
      <BotonVolver a="/listas-materiales" etiqueta="Volver a materiales creados" />
      <div className="cabecera-pagina">
        <div>
          <h1>{elemento.nombre}</h1>
          <p className="campo-nota">
            {ETIQUETA_TIPO[elemento.tipo]} · creado el {new Date(elemento.creadoEl).toLocaleDateString('es-MX')}
          </p>
        </div>
      </div>

      <section className="panel" style={{ marginBottom: 24 }}>
        <div className="panel-cabecera">
          <h2>Datos</h2>
        </div>
        <div className="panel-cuerpo">
          <div className="rejilla-campos">
            <div>
              <div className="indicador-titulo">Tipo</div>
              <div>{ETIQUETA_TIPO[elemento.tipo]}</div>
            </div>
            <div>
              <div className="indicador-titulo">Tipo de activo</div>
              <div>{elemento.tipoActivo ?? 'Todos los activos'}</div>
            </div>
            <div>
              <div className="indicador-titulo">Planeado en</div>
              <div>
                {elemento.usosOt} OT · {elemento.usosSolicitud} solicitud(es)
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: 24 }}>
        <div className="panel-cabecera" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <h2>Listas de materiales donde aparece</h2>
          <Link to="/listas-materiales" className="boton secundario pequeno">
            Editar listas
          </Link>
        </div>
        <div className="panel-cuerpo">
          {listas.isLoading ? <p className="campo-nota">Cargando…</p> : null}
          {listas.error ? <Aviso tono="error">No se pudieron cargar las listas.</Aviso> : null}
          {listas.data && listas.data.length === 0 ? (
            <p className="campo-nota">
              Todavía no está en ninguna lista. Se agrega desde Listas de materiales, eligiendo el tipo de activo y el tipo de
              trabajo.
            </p>
          ) : null}
          {listas.data && listas.data.length > 0 ? (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Tipo de activo</th>
                    <th>Tipo de trabajo</th>
                  </tr>
                </thead>
                <tbody>
                  {listas.data.map((l) => (
                    <tr key={`${l.tipoActivoId}-${l.tipoTrabajo ?? 'general'}`}>
                      <td>{l.tipoActivo}</td>
                      <td>{l.tipoTrabajo ? ETIQUETA_TIPO_TRABAJO[l.tipoTrabajo] : 'General (todos los trabajos)'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Dónde se planeó</h2>
        </div>
        <div className="panel-cuerpo">
          {usos.isLoading ? <p className="campo-nota">Cargando…</p> : null}
          {usos.error ? <Aviso tono="error">No se pudo cargar el detalle de uso.</Aviso> : null}
          {usos.data && usos.data.length === 0 ? (
            <p className="campo-nota">Todavía no aparece en ninguna OT ni solicitud.</p>
          ) : null}
          {usos.data && usos.data.length > 0 ? (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Origen</th>
                    <th>Número</th>
                    <th>Título</th>
                    <th>Estado</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {usos.data.map((u) => (
                    <tr key={`${u.origen}-${u.refId}`}>
                      <td>{u.origen === 'OT' ? 'OT' : 'Solicitud'}</td>
                      <td>
                        <Link to={u.origen === 'OT' ? `/ordenes/${u.refId}` : `/solicitudes/${u.refId}`}>#{u.numero}</Link>
                      </td>
                      <td>
                        <Link to={u.origen === 'OT' ? `/ordenes/${u.refId}` : `/solicitudes/${u.refId}`}>{u.titulo}</Link>
                      </td>
                      <td>{ETIQUETA_ESTADO[u.estado] ?? u.estado}</td>
                      <td>{u.cantidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
